/**
 * Direct Mail — which pages the estimate packet carries.
 *
 * Every page is another sheet to print, fold and post, so the selection here
 * is also the price of a piece. That number is shown next to the choice rather
 * than discovered at checkout.
 */

import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Sheet,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import toast from 'react-hot-toast';

import { Sparkle as AiIcon } from '@phosphor-icons/react';

import { DirectMailPageHeader } from '../../components/directmail/DirectMailNav';
import { directMailSettingsApi, formatMoney } from '../../lib/directMailApi';
import type { DirectMailSettingsPayload } from '../../lib/directMailApi';
import { getApiErrorMessage } from '../../lib/apiError';
import { useAuth } from '../../contexts/AuthContext';

export const DirectMailSettingsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canEdit = Boolean(user?.is_organization_admin || user?.is_superuser);

  const settingsQuery = useQuery({
    queryKey: ['direct-mail', 'settings'],
    queryFn: () => directMailSettingsApi.get(),
  });

  const [draft, setDraft] = useState<DirectMailSettingsPayload>({});

  // The server owns the saved state; the draft only holds what has been
  // touched since, so a save that lands elsewhere is not silently overwritten.
  useEffect(() => {
    setDraft({});
  }, [settingsQuery.data?.id]);

  const saved = settingsQuery.data;
  const value = <K extends keyof DirectMailSettingsPayload>(key: K): DirectMailSettingsPayload[K] =>
    (draft[key] !== undefined ? draft[key] : (saved?.[key] as DirectMailSettingsPayload[K]));

  const [drafting, setDrafting] = useState<string | null>(null);

  // The draft lands in the box for a person to read and edit. Nothing is saved
  // until they press save, because this copy goes on paper that costs a dollar
  // a page to mail.
  const draftCopy = async (page: 'promotions' | 'why_now', seed: string, field: 'promotions_text' | 'why_now_text') => {
    setDrafting(page);
    try {
      const { draft } = await directMailSettingsApi.generateCopy(page, seed);
      setDraft((prev) => ({ ...prev, [field]: draft }));
      toast.success('Draft written — read it before you save');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not write a draft'));
    } finally {
      setDrafting(null);
    }
  };

  const mutation = useMutation({
    mutationFn: (payload: DirectMailSettingsPayload) => directMailSettingsApi.update(payload),
    onSuccess: (next) => {
      queryClient.setQueryData(['direct-mail', 'settings'], next);
      setDraft({});
      toast.success('Packet pages saved');
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Could not save these pages')),
  });

  if (settingsQuery.isLoading) {
    return (
      <Box>
        <DirectMailPageHeader title="Packet pages" description="Choose what every mailed estimate includes." />
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      </Box>
    );
  }

  if (settingsQuery.isError || !saved) {
    return (
      <Box>
        <DirectMailPageHeader title="Packet pages" description="Choose what every mailed estimate includes." />
        <Alert color="danger" variant="soft">
          {getApiErrorMessage(settingsQuery.error, 'Could not load your packet settings')}
        </Alert>
      </Box>
    );
  }

  const dirty = Object.keys(draft).length > 0;
  const optional = saved.page_catalog.filter((page) => !page.required);
  const required = saved.page_catalog.filter((page) => page.required);

  // Priced from what is on screen, not from what is saved, so the cost moves
  // with the checkbox rather than after it.
  const selectedCount =
    required.length +
    optional.filter((page) => {
      const key = `include_${page.key}` as keyof DirectMailSettingsPayload;
      const chosen = value(key);
      if (page.key === 'promotions') {
        const text = (value('promotions_text') as string | undefined) ?? '';
        return Boolean(chosen) && text.trim().length > 0;
      }
      return Boolean(chosen);
    }).length;

  return (
    <Box>
      <DirectMailPageHeader
        title="Packet pages"
        description="Choose what every mailed estimate includes. Each page adds to the price of a piece."
      />

      {!canEdit && (
        <Alert color="neutral" variant="soft" sx={{ mb: 2 }}>
          Only an organization admin can change what the packet includes.
        </Alert>
      )}

      <Stack spacing={2}>
        <Card>
          <CardContent>
            <Typography level="title-md">Always included</Typography>
            <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 1 }}>
              The cover carries the address the piece is mailed to and the price. The last page tells the
              homeowner what to do next.
            </Typography>
            <Stack spacing={0.5}>
              {required.map((page) => (
                <Typography key={page.key} level="body-sm">
                  · {page.label}
                </Typography>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography level="title-md" sx={{ mb: 1 }}>
              Optional pages
            </Typography>

            <Stack spacing={2}>
              <Box>
                <Checkbox
                  label="Detailed estimate — how we got to this number"
                  checked={Boolean(value('include_detailed_estimate'))}
                  disabled={!canEdit}
                  onChange={(event) =>
                    setDraft((prev) => ({ ...prev, include_detailed_estimate: event.target.checked }))
                  }
                />
                <FormHelperText>The measurement, the waste factor and the price per square.</FormHelperText>
              </Box>

              <Box>
                <Checkbox
                  label="About us — credentials and reviews"
                  checked={Boolean(value('include_about_us'))}
                  disabled={!canEdit}
                  onChange={(event) => setDraft((prev) => ({ ...prev, include_about_us: event.target.checked }))}
                />
                <FormHelperText>
                  Your highest-rated recent reviews are printed unless you pick specific ones.
                </FormHelperText>
              </Box>

              <Box>
                <Checkbox
                  label="Promotions"
                  checked={Boolean(value('include_promotions'))}
                  disabled={!canEdit}
                  onChange={(event) => setDraft((prev) => ({ ...prev, include_promotions: event.target.checked }))}
                />
                <FormHelperText>An empty offer prints nothing and is not charged.</FormHelperText>
                {Boolean(value('include_promotions')) && (
                  <Stack spacing={1} sx={{ mt: 1, pl: 3 }}>
                    <FormControl>
                      <FormLabel>Headline</FormLabel>
                      <Input
                        value={(value('promotions_headline') as string) ?? ''}
                        disabled={!canEdit}
                        placeholder="$500 off a full replacement booked this month"
                        onChange={(event) =>
                          setDraft((prev) => ({ ...prev, promotions_headline: event.target.value }))
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>The offer</FormLabel>
                      <Textarea
                        minRows={3}
                        value={(value('promotions_text') as string) ?? ''}
                        disabled={!canEdit}
                        placeholder="In your own words — what the offer is, and how they claim it."
                        onChange={(event) => setDraft((prev) => ({ ...prev, promotions_text: event.target.value }))}
                      />
                      <Button
                        size="sm"
                        variant="plain"
                        startDecorator={<AiIcon />}
                        loading={drafting === 'promotions'}
                        disabled={!canEdit || !((value('promotions_headline') as string) ?? '').trim()}
                        onClick={() =>
                          draftCopy('promotions', (value('promotions_headline') as string) ?? '', 'promotions_text')
                        }
                        sx={{ alignSelf: 'flex-start', mt: 0.5, px: 0 }}
                      >
                        Write it for me
                      </Button>
                      <FormHelperText>Drafted from your headline and brand voice. Read it before saving.</FormHelperText>
                    </FormControl>
                  </Stack>
                )}
              </Box>

              <Box>
                <Checkbox
                  label="Why now — what waiting costs"
                  checked={Boolean(value('include_why_now'))}
                  disabled={!canEdit}
                  onChange={(event) => setDraft((prev) => ({ ...prev, include_why_now: event.target.checked }))}
                />
                <FormHelperText>Common failures and why they get more expensive. Leave the copy blank to use ours.</FormHelperText>
                {Boolean(value('include_why_now')) && (
                  <FormControl sx={{ mt: 1, pl: 3 }}>
                    <FormLabel>Your own words (optional)</FormLabel>
                    <Textarea
                      minRows={3}
                      value={(value('why_now_text') as string) ?? ''}
                      disabled={!canEdit}
                      onChange={(event) => setDraft((prev) => ({ ...prev, why_now_text: event.target.value }))}
                    />
                    <Button
                      size="sm"
                      variant="plain"
                      startDecorator={<AiIcon />}
                      loading={drafting === 'why_now'}
                      disabled={!canEdit}
                      onClick={() => draftCopy('why_now', '', 'why_now_text')}
                      sx={{ alignSelf: 'flex-start', mt: 0.5, px: 0 }}
                    >
                      Write it for me
                    </Button>
                  </FormControl>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Sheet variant="soft" color="primary" sx={{ borderRadius: 'md', p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
            <Box>
              <Typography level="title-md">
                {selectedCount} {selectedCount === 1 ? 'page' : 'pages'} per piece
              </Typography>
              <Typography level="body-sm">
                {saved.price_per_piece
                  ? `Currently ${formatMoney(saved.price_per_piece)} each — saved selection.`
                  : 'Per-piece pricing is unavailable right now.'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              {dirty && (
                <Button variant="plain" color="neutral" onClick={() => setDraft({})} disabled={mutation.isPending}>
                  Discard
                </Button>
              )}
              <Button
                loading={mutation.isPending}
                disabled={!canEdit || !dirty}
                onClick={() => mutation.mutate(draft)}
              >
                Save pages
              </Button>
            </Stack>
          </Stack>
          <Divider sx={{ my: 1.5 }} />
          <Typography level="body-xs">
            Pieces already in a batch keep the pages they were priced for — changing this only affects batches
            you create from now on.
          </Typography>
        </Sheet>
      </Stack>
    </Box>
  );
};

export default DirectMailSettingsPage;
